import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(undefined);

/**
 * ThemeProvider
 * ─────────────────────────────────────────────────────────────
 * Owns the dark/light theme state. The actual initial application
 * of the theme to <html> happens BEFORE React hydrates via the
 * inline FOUC-prevention script in `public/index.html`. This
 * provider then syncs React state to the DOM and persists changes.
 *
 * Premium AI platform defaults to dark mode for cohesive aesthetics
 * with the Cosmic SpaceBackdrop.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    const saved = localStorage.getItem('app_theme');
    return saved === 'light' || saved === 'dark' ? saved : 'dark';
  });

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
    html.classList.toggle('dark', theme === 'dark');
    // Make native form controls (scrollbars, inputs, autofill) follow the theme
    html.style.colorScheme = theme;
    try { localStorage.setItem('app_theme', theme); } catch { /* private mode */ }
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
